/**
 * Keep designs safe (plan docs/plans/tokenforge-keep-designs-safe-2026-11-03.md, route B; Ken 11-03): the customer's LITKO
 * (Shopify) login, carried to the designer by a one-time hop through litko.net.
 *
 * 1. `startLink` mints a one-time `state` (OMAR B1), keeps it in sessionStorage for 10 minutes and sends the tab to
 *    `litko.net/apps/litko/tokenforge/link?state=…&return=…`. The Worker there checks who is logged in (logging them in
 *    first when needed) and comes back to `design.litko.net/…#tf_link=<token>`.
 * 2. `captureLinkFragment` (main.tsx, before anything else runs) reads the token and strips it from the address bar (S4).
 * 3. The studio takes it with `takePendingLink`, which refuses a token this tab has no pending `state` for (B1: a link
 *    started in someone else's browser), and posts `{linkToken, state}` to Redtail (`POST /v1/engine/owners/link`, 1.26.0).
 *
 * Every storage read and write is guarded: blocked storage means no pending link.
 */

/** The engine release that links an owner to a LITKO account (K4). */
export const ACCOUNT_LINK_CONTRACT = '1.26.0';

/** Where the hop starts: the app proxy route the Worker serves (litko-content; Ken set the proxy to /apps/litko on 11-03). */
const DEFAULT_LINK_BASE = 'https://litko.net/apps/litko/tokenforge/link';

/** How long a started link may take, login included, before its `state` is no good. */
const STATE_TTL_MS = 10 * 60 * 1000;

const STATE_KEY = 'tf.linkState';

/** Where the Worker sends the customer back to (B4: a fixed key, never a URL). */
export type LinkReturn = 'designer' | 'mytokens';

/** The token's shape (`tfl1.<kid>.<payload>.<sig>`, all base64url); the server checks everything else. */
const LINK_TOKEN = /^tfl1\.[A-Za-z0-9_-]{1,64}\.[A-Za-z0-9_-]{1,800}\.[A-Za-z0-9_-]{1,200}$/;

/** `VITE_ACCOUNT_LINK`: `on`, or `dev` for DEV builds only. Off by default until the Worker route exists. */
export function accountLinkConfigured(): boolean {
    const mode = import.meta.env.VITE_ACCOUNT_LINK ?? 'off';
    return mode === 'on' || (mode === 'dev' && import.meta.env.DEV);
}

function linkBase(): string {
    return import.meta.env.VITE_ACCOUNT_LINK_BASE ?? DEFAULT_LINK_BASE;
}

function randomState(): string {
    const b = crypto.getRandomValues(new Uint8Array(16));
    return btoa(String.fromCharCode(...b)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

interface StoredState {
    state: string;
    expiresAt: number;
    returnTo: LinkReturn;
}

/** The URL that starts the hop, with a fresh `state` kept for this tab. Null when sessionStorage is blocked (no way to check the return). */
export function linkStartUrl(returnTo: LinkReturn, now: number = Date.now()): string | null {
    const state = randomState();
    try {
        window.sessionStorage.setItem(STATE_KEY, JSON.stringify({ state, expiresAt: now + STATE_TTL_MS, returnTo } satisfies StoredState));
    } catch {
        return null;
    }
    const q = new URLSearchParams({ state, return: returnTo });
    return `${linkBase()}?${q.toString()}`;
}

/** Start the hop (a top-level navigation, never a fetch or an iframe). False when it can't be started here. */
export function startLink(returnTo: LinkReturn): boolean {
    const url = linkStartUrl(returnTo);
    if (url === null) {
        return false;
    }
    window.location.assign(url);
    return true;
}

let pendingToken: string | null = null;

/** The token in a `#tf_link=` fragment, or null. */
export function linkTokenFromHash(hash: string): string | null {
    const m = /^#tf_link=(.+)$/.exec(hash);
    return m !== null && LINK_TOKEN.test(m[1]) ? m[1] : null;
}

/**
 * Read `#tf_link=<token>` once and take it out of the address bar (S4), before any other script runs. A malformed
 * `tf_link` fragment is stripped too and never used.
 */
export function captureLinkFragment(): void {
    if (!window.location.hash.startsWith('#tf_link=')) {
        return;
    }
    pendingToken = linkTokenFromHash(window.location.hash);
    try {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
    } catch {
        // An old browser keeps the fragment; the token is single use and bound to this tab's state anyway.
    }
}

export type PendingLink =
    | { kind: 'ready'; linkToken: string; state: string; returnTo: LinkReturn }
    /** A token came back but this tab never started a link, or its 10 minutes ran out (B1). */
    | { kind: 'not_started_here' };

/** The link waiting for this page, once; null when there is none. The `state` stays stored until `finishLink`. */
export function takePendingLink(now: number = Date.now()): PendingLink | null {
    const token = pendingToken;
    pendingToken = null;
    if (token === null) {
        return null;
    }
    const stored = readState();
    if (stored === null || stored.expiresAt < now) {
        finishLink();
        return { kind: 'not_started_here' };
    }
    return { kind: 'ready', linkToken: token, state: stored.state, returnTo: stored.returnTo };
}

/** The state is single use: drop it once the link is done, refused or abandoned. */
export function finishLink(): void {
    try {
        window.sessionStorage.removeItem(STATE_KEY);
    } catch {
        // Nothing to do.
    }
}

function readState(): StoredState | null {
    try {
        const raw = window.sessionStorage.getItem(STATE_KEY);
        if (raw === null) {
            return null;
        }
        const s = JSON.parse(raw) as Partial<StoredState>;
        if (typeof s.state !== 'string' || typeof s.expiresAt !== 'number' || (s.returnTo !== 'designer' && s.returnTo !== 'mytokens')) {
            return null;
        }
        return { state: s.state, expiresAt: s.expiresAt, returnTo: s.returnTo };
    } catch {
        return null;
    }
}

/** For tests: put a token in as if the page had just loaded with it. */
export function setPendingLinkForTest(token: string | null): void {
    pendingToken = token;
}
